import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NotificationService } from '@app/core';
import { CreateTagData, TagData, TagSelectorData } from '@app/models';
import { FORM_REGEX } from '@app/shared/data/form-regex';
import { GENERAL_MESSAGES, SNACKBAR_ACTIONS } from '@app/shared/data/messages';

@Component({
    selector: 'app-tag-selector',
    templateUrl: './tag-selector.component.html',
    styleUrls: ['./tag-selector.component.scss'],
    standalone: false
})
export class TagSelectorComponent implements OnInit {

  generalMessages = GENERAL_MESSAGES;
  @Input('tagData') tagData: TagSelectorData | null = null;
  @Input('maxLimit') maxLimit: number = 5;
  @Input('disabled') disabled: boolean = false;
  @Output('valueChanges') valueChanges: EventEmitter<any> = new EventEmitter<any>();
  @Output('createTagEmitter') createTagEmitter: EventEmitter<CreateTagData> = new EventEmitter<CreateTagData>();
  @Output('tagsCleared') tagsCleared: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild('sheetTemp') sheetTemp!: TemplateRef<any>;
  
  tagValues: {id: number; name: string; }[] = [];
  tagListValues: {id: number; name: string; checked: boolean; }[] = [];
  
  searchInputValue: string = "";
  searchInputErrors: string[] = [];
  searchPanelOpened: boolean = false;
  maxLimitErrorFlag: boolean = false;
  createRequestInProgress: boolean = false;

  constructor(
    private readonly _bottomSheet: MatBottomSheet, 
    private readonly _notificationService: NotificationService, 
  ) {}

  openSheet(): void {
    this.setCurrentValues();
    this._bottomSheet.open(this.sheetTemp, { panelClass: 'tag-selector-sheet', disableClose: true });
  }

  setCurrentValues(checkedTags: number[] = []) {
    let prevSelectedIDs = Array.from(new Set([
      ...this.tagValues.map(tag => tag.id),
      ...checkedTags
    ]));
    this.tagListValues = this.tagData?.availableTags?.map(tag => ({
        ...tag, 
        checked: prevSelectedIDs.includes(tag.id)
      })) || [];
  }

  ngOnInit(): void {
    this.initiateValues();
  }

  initiateValues(data: TagSelectorData | null = this.tagData) {
    this.resetSearch();
    this.tagData = data;
    this.tagValues = [];
    this.tagListValues = [];
    data?.availableTags?.forEach(tag => {
      if (data.values.includes(tag.id)) {
        this.tagValues = [ ...this.tagValues, {...tag} ];
      }
    });
  }

  closeForm(resetValues: boolean = false) {
    if (resetValues) {
      let prevSelectedIDs = this.tagValues.map(tag => tag.id);
      this.tagListValues = this.tagListValues.map(tag => ({ ...tag, checked: prevSelectedIDs.includes(tag.id) }));
    } else {
      this.tagValues = [...this.tagListValues.filter(tag => tag.checked).map(tag => ({ id: tag.id, name: tag.name }))];
    }
    this._bottomSheet.dismiss();
    this.valueChanges.emit();
  }

  removeTag(id: number) {
    this.tagValues = this.tagValues.filter(tag => (tag.id != id));
  }

  listChange(checked: boolean, id: number) {
    this.maxLimitErrorFlag = (checked && this.reachedMaxLimit);
    if (!this.maxLimitErrorFlag) {
      this.tagListValues = this.tagListValues.map(tag => (tag.id === id) ? {...tag, checked: !!checked} : tag);
    }
  }

  clearAll() {
    const previousTagCount = this.tagListValues.filter(tag => tag.checked).length;
    this.tagListValues = this.tagListValues.map(tag => ({...tag, checked: false }));
    
    // Emit event for parent component to track analytics
    if (previousTagCount > 0) {
      this.tagsCleared.emit(previousTagCount);
    }
  }

  reset() {
    this.initiateValues(this.tagData);
  }

  get selectedTags() {
    return this.tagValues;
  }

  get selectedTagIds() {
    return this.selectedTags.map(tag => tag.id).sort((a,b)=>a-b);
  }

  get reachedMaxLimit(): boolean {
    return this.tagListValues.filter(tag => !!tag.checked).length >= this.maxLimit;
  }

  trackByFn(index: number, item: any): number { 
    return item.id; 
  }

  get checkedTags() {
    return this.tagListValues.filter(tag => tag.checked);
  }

  get searchTags() {
    return this.tagListValues.filter(tag => tag.name.toLowerCase().includes(this.searchInputValue.toLowerCase()));
  }

  openSearchPanel() {
    this.searchPanelOpened = true;
    this.maxLimitErrorFlag = this.reachedMaxLimit;
  }

  createTag() {
    if (this.searchInputErrors.length > 0) return;
    this.setCreateRequestProgress(true);
    this.createTagEmitter.emit({name: this.searchInputValue.trim()});
  }

  handleCreateTagResponse(tagsData: TagData[], checkedTagIds: number[]) {
    this.tagData = this.tagData
     ? {
        ...this.tagData,
        availableTags: [...tagsData]
      }
     : null;
    this.setCurrentValues(checkedTagIds);
    this.setCreateRequestProgress(false);
    this._notificationService.showSuccess(`New tag "${this.searchInputValue}" created successfully`, SNACKBAR_ACTIONS.OK);
  }

  handleCreateTagError(error: HttpErrorResponse, checkedTagIds: number[]) {
    this.setCurrentValues(checkedTagIds);
    this.setCreateRequestProgress(false);
    this._notificationService.showError(error, SNACKBAR_ACTIONS.OK);
  }

  resetSearch() {
    this.searchInputValue = "";
    this.searchPanelOpened = false;
    this.maxLimitErrorFlag = false;
    this.searchInputErrors = [];
  }

  getValidateTagPattern(): void {
    this.searchInputErrors = [];
    if (!this.searchInputValue.length) return;

    if (this.searchInputValue.length < 4) {
      this.searchInputErrors.push('Enter minimum 4 alphanumeric characters.');
    } 
    if(!FORM_REGEX.tagPattern.test(this.searchInputValue)) {
      this.searchInputErrors.push('Only letters, numbers, spaces, and underscores are allowed in the tag names.');
    }
  }

  setCreateRequestProgress(value: boolean = !this.createRequestInProgress) {
    this.createRequestInProgress = value;
  }

}